// web/src/components/icons/index.tsx
// Inline SVG icon set. All icons are 24×24, 1.5px stroke, currentColor.
// Designed to match the editorial-football vibe — outline-only, geometric,
// no gradients or filled fills (except where the shape demands it, e.g.
// the captain armband band).
//
// Usage:
//   import { IconGoal, IconStar } from '../components/icons';
//   <IconGoal size={20} aria-hidden />
//
// `size` defaults to 24. `aria-hidden` should be set when the icon is
// decorative; if it's the only label, pass `title` and `role="img"` instead.

import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number;
}

const base = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

// ─── Event icons ──────────────────────────────────────────────────────────

export function IconGoal({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Football — pentagon-and-hexagon classic ball */}
      <circle cx="12" cy="12" r="9" />
      <polygon points="12,7 15,9.2 13.8,12.8 10.2,12.8 9,9.2" />
      <line x1="12" y1="7" x2="12" y2="3" />
      <line x1="15" y1="9.2" x2="18.5" y2="7.5" />
      <line x1="13.8" y1="12.8" x2="16.5" y2="16" />
      <line x1="10.2" y1="12.8" x2="7.5" y2="16" />
      <line x1="9" y1="9.2" x2="5.5" y2="7.5" />
    </svg>
  );
}

export function IconAssist({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Two arrows passing — one player to another */}
      <path d="M3 8 L11 8 L8 5" />
      <path d="M21 16 L13 16 L16 19" />
    </svg>
  );
}

export function IconShotOn({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Arrow into a net frame */}
      <rect x="14" y="5" width="6" height="14" />
      <path d="M3 12 L17 12 L13 8 M17 12 L13 16" />
    </svg>
  );
}

export function IconShotOff({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Arrow past the net */}
      <rect x="14" y="9" width="6" height="10" />
      <path d="M3 8 L20 4 L16 4 M20 4 L20 8" />
    </svg>
  );
}

export function IconSave({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Goalkeeper gloves — palm spread */}
      <path d="M7 14 L7 7 A1.5 1.5 0 0 1 10 7 L10 11" />
      <path d="M10 11 L10 5 A1.5 1.5 0 0 1 13 5 L13 11" />
      <path d="M13 11 L13 6 A1.5 1.5 0 0 1 16 6 L16 12" />
      <path d="M16 12 L16 8 A1.5 1.5 0 0 1 19 8 L19 14 A6 6 0 0 1 7 14 Z" />
    </svg>
  );
}

export function IconDefensive({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Shield with a centre line */}
      <path d="M12 3 L20 6 L20 12 C20 17 12 21 12 21 C12 21 4 17 4 12 L4 6 Z" />
      <line x1="12" y1="8" x2="12" y2="15" />
    </svg>
  );
}

// ─── Player icons ─────────────────────────────────────────────────────────

export function IconStar({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <polygon points="12,3 14.5,9 21,9.5 16,14 17.5,20.5 12,17 6.5,20.5 8,14 3,9.5 9.5,9" />
    </svg>
  );
}

export function IconCaptainArmband({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Armband — open band with a stripe */}
      <rect x="4" y="9" width="16" height="6" rx="1" />
      <line x1="4" y1="12" x2="20" y2="12" />
    </svg>
  );
}

export function IconBoot({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Football boot */}
      <path d="M4 16 L4 11 L9 11 L13 7 L18 8 L20 11 L20 16 L4 16 Z" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <line x1="9" y1="11" x2="9" y2="14" />
      <line x1="12" y1="11" x2="12" y2="14" />
      <line x1="15" y1="11" x2="15" y2="14" />
    </svg>
  );
}

export function IconPitch({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <rect x="2.5" y="4" width="19" height="16" rx="1" />
      <line x1="12" y1="4" x2="12" y2="20" />
      <circle cx="12" cy="12" r="3" />
      <rect x="2.5" y="9" width="3" height="6" />
      <rect x="18.5" y="9" width="3" height="6" />
    </svg>
  );
}

export function IconWhistle({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <path d="M3 10 L9 10 L13 7 L17 7 L20 10 L20 14 L17 17 L13 17 L9 14 L3 14 Z" />
      <circle cx="16" cy="12" r="1.2" />
    </svg>
  );
}

// ─── Match-type icons ─────────────────────────────────────────────────────

export function IconLeague({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Trophy on stand */}
      <path d="M8 4 L16 4 L16 9 A4 4 0 0 1 8 9 Z" />
      <path d="M16 6 L19 6 L19 8 A2 2 0 0 1 17 10" />
      <path d="M8 6 L5 6 L5 8 A2 2 0 0 0 7 10" />
      <line x1="12" y1="13" x2="12" y2="17" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="10" y1="17" x2="14" y2="17" />
    </svg>
  );
}

export function IconCup({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Big trophy cup */}
      <path d="M7 4 L17 4 L17 11 A5 5 0 0 1 7 11 Z" />
      <path d="M17 5 L20 5 L20 8 A3 3 0 0 1 17 11" />
      <path d="M7 5 L4 5 L4 8 A3 3 0 0 0 7 11" />
      <line x1="12" y1="16" x2="12" y2="20" />
      <rect x="8" y="20" width="8" height="1" />
    </svg>
  );
}

export function IconFriendly({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Two interlocking circles — friendly handshake */}
      <circle cx="9" cy="12" r="4" />
      <circle cx="15" cy="12" r="4" />
    </svg>
  );
}

export function IconTournament({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Bracket */}
      <path d="M3 6 L7 6 L7 11 L11 11" />
      <path d="M3 18 L7 18 L7 13 L11 13" />
      <path d="M11 12 L15 12 L15 8 L21 8" />
      <path d="M15 12 L15 16 L21 16" />
    </svg>
  );
}

export function IconTraining({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      {/* Stopwatch */}
      <circle cx="12" cy="14" r="7" />
      <line x1="10" y1="3" x2="14" y2="3" />
      <line x1="12" y1="14" x2="15" y2="11" />
      <line x1="12" y1="9" x2="12" y2="10" />
    </svg>
  );
}

// ─── UI icons ─────────────────────────────────────────────────────────────

export function IconPlus({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export function IconEdit({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <path d="M4 20 L4 16 L16 4 L20 8 L8 20 Z" />
      <line x1="14" y1="6" x2="18" y2="10" />
    </svg>
  );
}

export function IconDelete({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <path d="M5 7 L19 7 L18 20 L6 20 Z" />
      <line x1="9" y1="4" x2="15" y2="4" />
      <line x1="3" y1="7" x2="21" y2="7" />
    </svg>
  );
}

export function IconShare({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <line x1="8.2" y1="11" x2="15.8" y2="7.2" />
      <line x1="8.2" y1="13" x2="15.8" y2="16.8" />
    </svg>
  );
}

export function IconCamera({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <path d="M3 8 L7 8 L9 5 L15 5 L17 8 L21 8 L21 19 L3 19 Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

export function IconDownload({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <line x1="12" y1="4" x2="12" y2="15" />
      <path d="M7 11 L12 16 L17 11" />
      <line x1="4" y1="20" x2="20" y2="20" />
    </svg>
  );
}

export function IconArrowLeft({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <line x1="20" y1="12" x2="4" y2="12" />
      <path d="M10 6 L4 12 L10 18" />
    </svg>
  );
}

export function IconCheck({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <path d="M4 12 L10 18 L20 6" />
    </svg>
  );
}

export function IconX({ size = 24, ...p }: IconProps) {
  return (
    <svg {...base(size)} {...p}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

// Map for event-type → icon component, used by Timeline + MatchCard
export const EVENT_ICONS = {
  goal: IconGoal,
  assist: IconAssist,
  shot_on: IconShotOn,
  shot_off: IconShotOff,
  save: IconSave,
  defensive: IconDefensive,
} as const;

// Map for match-type → icon component
export const MATCH_TYPE_ICONS = {
  league: IconLeague,
  cup: IconCup,
  friendly: IconFriendly,
  tournament: IconTournament,
  training: IconTraining,
} as const;
